import { Alert, Box, Button, Typography } from '@mui/material';
import { useEffect, useId, useImperativeHandle, useState } from 'react';
import type { Ref } from 'react';
import type { CardPaymentInput, PaymentConfigDto } from '@app/shared';

/**
 * A new card through Authorize.Net's hosted form (Accept Hosted UI). The button opens the form,
 * which is Authorize.Net's own page in a frame: the number is typed there and 212Ez gets a one-time
 * token. Its script finds the button by the class AcceptUI when it loads, so it is loaded after
 * the button is on the page, and calls back a global function named on the button.
 */
export interface AcceptAnswer {
  opaqueData?: { dataDescriptor: string; dataValue: string };
  messages?: { resultCode?: string; message?: Array<{ text?: string }> };
  encryptedCardData?: { cardNumber?: string; expDate?: string };
}

type Global = Record<string, ((r: AcceptAnswer) => void) | undefined>;

export function AuthorizeNetCard({
  handle,
  config,
  save,
}: {
  handle: Ref<{ collect: () => Promise<CardPaymentInput> }>;
  config: NonNullable<PaymentConfigDto['authorizeNet']>;
  save: boolean;
}) {
  const id = useId().replace(/[^A-Za-z0-9]/g, '');
  const responseHandler = `acceptCard${id}`;
  const [token, setToken] = useState<{ value: string; label: string } | null>(null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    const global = window as unknown as Global;
    global[responseHandler] = (answer) => {
      const data = answer.opaqueData;
      if (answer.messages?.resultCode !== 'Ok' || !data) {
        setProblem(answer.messages?.message?.[0]?.text ?? 'The card form did not answer.');
        return;
      }
      setProblem('');
      setToken({
        value: `opaque:${data.dataDescriptor}:${data.dataValue}`,
        label: `Card ending ${(answer.encryptedCardData?.cardNumber ?? '').slice(-4)}`,
      });
    };
    const host = config.sandbox ? 'jstest.authorize.net' : 'js.authorize.net';
    const script = document.createElement('script');
    script.src = `https://${host}/v3/AcceptUI.js`;
    script.charset = 'utf-8';
    document.body.appendChild(script);
    return () => {
      script.remove();
      global[responseHandler] = undefined;
    };
  }, [config.sandbox, responseHandler]);

  useImperativeHandle(
    handle,
    () => ({
      collect: async () => {
        if (!token) throw new Error('Press "Enter card details" first.');
        return { paymentMethodId: token.value, save };
      },
    }),
    [token, save],
  );

  return (
    <Box sx={{ display: 'grid', gap: 1, justifyItems: 'start' }} aria-label="Card details">
      {token ? (
        <Typography variant="body2">{token.label} entered; it is charged on submit.</Typography>
      ) : null}
      <Button
        type="button"
        variant="outlined"
        className="AcceptUI"
        data-billingaddressoptions='{"show":false,"required":false}'
        data-apiloginid={config.apiLoginId}
        data-clientkey={config.clientKey}
        data-acceptuiformbtntxt="Use this card"
        data-acceptuiformheadertxt="Card details"
        data-paymentoptions='{"showCreditCard":true,"showBankAccount":false}'
        data-responsehandler={responseHandler}
      >
        {token ? 'Use another card' : 'Enter card details'}
      </Button>
      {problem ? (
        <Alert severity="error" sx={{ width: '100%' }}>
          {problem}
        </Alert>
      ) : null}
    </Box>
  );
}
